import {
  Badge,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { offerApi } from '@texas/api/endpoints/offerApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { Locale } from '@texas/i18n/types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  tierPriceId: number | null;
  currencyCode: string;
  isOpen: boolean;
  onClose: () => void;
}

export function TierPriceHistoryModal({
  tierPriceId,
  currencyCode,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useApiResource(
    offerApi.getTierPriceHistory,
  );

  useEffect(() => {
    if (isOpen && tierPriceId !== null) refetch(tierPriceId);
  }, [isOpen, tierPriceId, refetch]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('offer.priceHistory')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {loading && <Spinner size="sm" />}
          {error && <ErrorDetails error={error} />}
          {data && (
            <Flex direction="column" gap={0}>
              {data.map((entry, idx) => (
                <Flex key={entry.id} gap={3} align="flex-start">
                  <Flex direction="column" align="center" flexShrink={0}>
                    <Box
                      w="10px"
                      h="10px"
                      borderRadius="full"
                      bg={idx === 0 ? 'blue.400' : 'gray.500'}
                      mt="4px"
                    />
                    {idx < data.length - 1 && (
                      <Box w="2px" flex="1" bg="gray.600" minH="24px" />
                    )}
                  </Flex>
                  <Box pb={4}>
                    <Flex align="center" gap={2} mb={0.5}>
                      <Text fontSize="sm" fontWeight="medium">
                        {entry.price != null
                          ? `${entry.price} ${currencyCode}`
                          : '—'}
                      </Text>
                      {idx === 0 && (
                        <Badge colorScheme="blue" fontSize="2xs">
                          {t('general.current')}
                        </Badge>
                      )}
                      {entry.acceptedDate && (
                        <Badge colorScheme="green" fontSize="2xs">
                          {t('general.accepted')}
                        </Badge>
                      )}
                    </Flex>
                    {entry.acceptedDate && (
                      <Text fontSize="xs" color="gray.400">
                        {entry.acceptedByUserName ?? 'Unknown'} ·{' '}
                        {formatDate(Locale.En, entry.acceptedDate)}
                      </Text>
                    )}
                    {!entry.acceptedDate && idx === 0 && (
                      <Text fontSize="xs" color="gray.500">
                        {t('offer.notYetAccepted')}
                      </Text>
                    )}
                  </Box>
                </Flex>
              ))}
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
